import { Result } from 'true-myth';
import {
  TEDUrvalConditionType,
  TEDUrvalType,
  TEDUrvalSidaType,
  TEDUrvalValueConditionType,
  TEDUrvalExtendedConditionType,
  TEDUrvalBooleanConditionType,
  TEDUrvalEnumConditionType,
  NodeUrvalType,
  IFilterConditionObject,
  TEDUrvalFilterConditionType,
} from '../../../Types/UrvalTypes';

import { assoc, equals, toString } from 'ramda';
import shortid from 'shortid';

import { getChecklistValuesAndLabels } from './urvalUtils';
import {
  IClientAssociationModel,
  IClientAttributeModel,
} from '../../../Types/ClientModelTypes';
import {
  findAssociations,
  getAttribute,
  findCollection,
  getAssociation,
} from '../../../Model/avenyModelUtils';
import {
  IUIPage,
  IStringConditionObject,
  INumericConditionObject,
  IBooleanConditionObject,
  ITypeTreeConditionObject,
  IChecklistConditionObject,
  IExtendedConditionObject,
  IUrvalCondition,
  UIUrvalConditionObjectType,
  IUIUrval,
  TypeTreeType,
} from '../../../Types/UrvalTypes';
import { createConditionID } from '../Components/UrvalFieldPicker/Utils/UrvalConditionUtils';
import {
  isNumericType,
  isStringType,
} from '../Components/SavedUrval/Utils/convertSavedUrval';
import { AvenyCollectionMap } from '../../../Model/ModelContext/ModelContext';
import Logger from '../../../Utils/Logger';
import { URVAL_CHECKLIST_NO_VALUE_VALUE } from '../Consts/UrvalConsts';
const ok = Result.ok;
const err = Result.err;
const getStringCondition = (
  valueCondition: TEDUrvalValueConditionType
): IUrvalCondition => {
  return {
    id: shortid.generate(),
    op: valueCondition.op,
    values: valueCondition.value
      ? valueCondition.value.map(value => ({
          id: shortid.generate(),
          value: value.value ? value.value : '',
          toValue: value.tovalue ? value.tovalue : undefined,
        }))
      : [{ id: shortid.generate() }],
  };
};

const getNumericCondition = (
  valueCondition: TEDUrvalValueConditionType
): IUrvalCondition => ({
  id: shortid.generate(),
  op: valueCondition.op,
  values: valueCondition.value
    ? valueCondition.value.map(value => ({
        id: shortid.generate(),
        value: value.value
          ? parseFloat(value.value.replace(',', '.'))
          : undefined,
        toValue: value.tovalue
          ? parseFloat(value.tovalue.replace(',', '.').replace(/ /g, ''))
          : undefined,
      }))
    : [{ id: shortid.generate() }],
});

const getNumericConditionObject = (
  attribute: IClientAttributeModel,
  condition: TEDUrvalValueConditionType,
  id: string,
  modelPath: IClientAssociationModel[]
): INumericConditionObject => ({
  id,
  modelPath,
  attribute,
  condition: [getNumericCondition(condition)],
  type: 'Numeric',
});

const getStringConditionObject = (
  attribute: IClientAttributeModel,
  condition: TEDUrvalValueConditionType,
  id: string,
  modelPath: IClientAssociationModel[]
): IStringConditionObject => ({
  id,
  modelPath,
  attribute,
  condition: [getStringCondition(condition)],
  type: 'String',
});

const getBooleanConditionObject = (
  attribute: IClientAttributeModel,
  condition: TEDUrvalBooleanConditionType | null | undefined,
  id: string,
  modelPath: IClientAssociationModel[]
): IBooleanConditionObject => {
  return {
    type: 'Boolean',
    id,
    modelPath,
    attribute,
    condition:
      condition == null
        ? { yes: false, no: false }
        : {
            yes: condition.yes ? condition.yes : false,
            no: condition.no ? condition.no : false,
          },
  };
};

const getFilterConditionObject = (
  attribute: IClientAttributeModel,
  condition: TEDUrvalFilterConditionType | null | undefined,
  id: string,
  modelPath: IClientAssociationModel[]
): IFilterConditionObject => {
  return {
    type: 'Filter',
    id,
    modelPath,
    attribute,
    condition: Boolean(condition?.active),
  };
};

const getTypeTreeConditionObject = (
  attribute: IClientAttributeModel,
  condition: TEDUrvalEnumConditionType,
  typeTree: TypeTreeType,
  id: string,
  modelPath: IClientAssociationModel[]
): ITypeTreeConditionObject => {
  return {
    type: 'TypeTree',
    id,
    attribute,
    typeTree,
    modelPath,
    condition: condition.selected
      ? (condition.selected.filter(value => value != null) as string[])
      : [],
    implicitCondition: [],
  };
};

const getChecklistConditionObject = (
  attribute: IClientAttributeModel,
  condition: TEDUrvalEnumConditionType,
  id: string,
  modelPath: IClientAssociationModel[]
): IChecklistConditionObject => {
  const checklistValues = getChecklistValuesAndLabels(attribute).map(
    valueAndLabel => valueAndLabel.value
  );
  return {
    type: 'Checklist',
    id,
    attribute,
    modelPath,
    condition: condition.selected
      ? condition.selected.map(value => {
          if (value == null) {
            return URVAL_CHECKLIST_NO_VALUE_VALUE;
          } else {
            const checklistValuesIndex = checklistValues.findIndex(
              equals(value)
            );
            if (checklistValuesIndex === -1) {
              Logger.error(`Could not convert value: ${value}`);
              return '';
            } else {
              return checklistValues[checklistValuesIndex];
            }
          }
        })
      : [],
  };
};

const getExtendedConditionObject = (
  association: IClientAssociationModel,
  extendedCondition: TEDUrvalExtendedConditionType | undefined | null,
  id: string,
  modelPath: IClientAssociationModel[],
  collectionMap: AvenyCollectionMap
): Result<IExtendedConditionObject | undefined, string> => {
  if (extendedCondition == null) {
    return err(
      'Tried to convert extended condition but extendedCondition is null'
    );
  }
  const pages: Result<IUIPage[], string> = extendedCondition.extconditions
    ? extendedCondition.extconditions.reduce(
        convertTEDUrvalSida(
          association.AssociatedCollectionName,
          collectionMap
        ),
        ok([])
      )
    : ok([]);
  return pages.map(p => ({
    type: 'Extended',
    association,
    id,
    modelPath,
    condition: [
      {
        id: shortid.generate(),
        op: extendedCondition.extop,
        in: p,
      },
    ],
  }));
};

const getConditionObject = (
  condition: TEDUrvalConditionType,
  conditionCollectionName: string,
  associations: IClientAssociationModel[],
  collectionMap: AvenyCollectionMap
): Result<UIUrvalConditionObjectType | undefined, string> => {
  let conditionObject: Result<UIUrvalConditionObjectType | undefined, string>;
  const id = createConditionID(condition.namn, associations);

  if (condition.typ === 'Extended') {
    const association = getAssociation(
      conditionCollectionName,
      '',
      condition.namn,
      collectionMap
    );

    if (association) {
      conditionObject = getExtendedConditionObject(
        association,
        condition.extendedCondition,
        id,
        associations,
        collectionMap
      );
    } else {
      return err(
        `Could not find association with name: ${condition.namn} in collection with name: ${conditionCollectionName}`
      );
    }
  } else {
    const attribute = getAttribute(
      conditionCollectionName,
      '',
      condition.namn,
      collectionMap
    );

    if (attribute) {
      // const type = getType(attribute, condition);

      if (condition.typ === 'Value' && condition.valueCondition != null) {
        if (isNumericType(attribute.DataType)) {
          conditionObject = ok(
            getNumericConditionObject(
              attribute,
              condition.valueCondition,
              id,
              associations
            )
          );
        } else if (isStringType(attribute.DataType)) {
          conditionObject = ok(
            getStringConditionObject(
              attribute,
              condition.valueCondition,
              id,
              associations
            )
          );
        } else {
          return err(
            `Data type: ${attribute.DataType} is neither Numeric nor String`
          );
        }
      } else if (condition.typ === 'Boolean') {
        conditionObject = ok(
          getBooleanConditionObject(
            attribute,
            condition.booleanCondition,
            id,
            associations
          )
        );
      } else if (condition.typ === 'Filter') {
        conditionObject = ok(
          getFilterConditionObject(
            attribute,
            condition.filterCondition,
            id,
            associations
          )
        );
      } else if (condition.typ === 'Enum' && condition.enumCondition != null) {
        if (attribute.AttributeName === '_typ') {
          const collection = findCollection(
            collectionMap,
            conditionCollectionName
          );
          let typeTree: TypeTreeType;
          if (collection) {
            typeTree = collection.RootType;
          } else {
            return err(
              `Could not find collection with name: ${conditionCollectionName} `
            );
          }

          conditionObject = ok(
            getTypeTreeConditionObject(
              attribute,
              condition.enumCondition,
              typeTree,
              id,
              associations
            )
          );
        } else {
          conditionObject = ok(
            getChecklistConditionObject(
              attribute,
              condition.enumCondition,
              id,
              associations
            )
          );
        }
      } else {
        return err(`Could not convert condition: ${toString(condition)}`);
      }
    } else {
      return err(
        `Could not find attribute with name: ${condition.namn} in collection with name: ${conditionCollectionName}`
      );
    }
  }
  return conditionObject;
};

const convertCondition =
  (collectionName: string, collectionMap: AvenyCollectionMap) =>
  (
    conditionObjects: UIUrvalConditionObjectType[],
    condition: TEDUrvalConditionType
  ): Result<UIUrvalConditionObjectType[], string> => {
    // Change name TYP to _typ
    if (condition.namn === 'TYP') {
      condition = assoc('namn', '_typ', condition);
    }

    const associations = condition.ass
      ? findAssociations(collectionName, condition.ass, collectionMap)
      : [];

    const conditionCollectionName =
      associations.length > 0
        ? associations[associations.length - 1].AssociatedCollectionName
        : collectionName;

    const conditionObject = getConditionObject(
      condition,
      conditionCollectionName,
      associations,
      collectionMap
    );

    return conditionObject.map(condobj =>
      condobj ? conditionObjects.concat(condobj) : conditionObjects
    );
  };

const convertTEDUrvalSida =
  (
    collectionName: string,
    collectionMap: AvenyCollectionMap,
    savedUrval?: NodeUrvalType
  ) =>
  (
    sidaList: Result<IUIPage[], string>,
    tedUrvalSida: TEDUrvalSidaType,
    currentIndex: number
  ): Result<IUIPage[], string> => {
    if (tedUrvalSida == null) {
      return sidaList;
    }
    const conditionobjects = tedUrvalSida.reduce(
      (acc: Result<UIUrvalConditionObjectType[], string>, curr) =>
        acc.andThen(x =>
          convertCondition(collectionName, collectionMap)(x, curr)
        ),
      ok<UIUrvalConditionObjectType[], string>([])
    );
    if (conditionobjects.isOk()) {
      let pageName =
        savedUrval?.conditionPageNames?.[currentIndex] ?? undefined;
      if (pageName === '') {
        pageName = undefined;
      }
      const sida: IUIPage = {
        id: shortid.generate(),
        conditionObjects: conditionobjects.value,
        pageName,
      };

      return sidaList.map(list => list.concat(sida));
    } else {
      return err(conditionobjects.error);
    }
  };

export const convertTEDUrvalToUIUrval = (
  collectionName: string,
  tedUrval: TEDUrvalType,
  collectionMap: AvenyCollectionMap,
  savedUrval?: NodeUrvalType
): Result<IUIUrval, string> => {
  if (tedUrval == null) {
    return ok({ pages: [{ conditionObjects: [], id: shortid.generate() }] });
  }
  const pages = tedUrval.reduce(
    convertTEDUrvalSida(collectionName, collectionMap, savedUrval),
    ok([])
  );

  return pages.map(p => ({ pages: p }));
};
